import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import { toLonLat } from 'ol/proj';
import React, { useEffect, useRef, useState } from 'react';
import { RiArrowRightUpLine } from 'react-icons/ri';
import { useStore } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import styles from './common.module.scss';

import { FieldNumber } from '@/components/ui/kit/Field';
import LabelGroup from '@/components/ui/LabelGroup';
import { MODE } from '@/define/robotStatus';
import OlMap from '@/helpers/OlMap';
import useFlightControlCommand from '@/hooks/command/dji/useFlightControlCommand';
import { NotifierService as notifier } from '@/libs/Notifier';
import { executeDJICommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

const Panel = ({ data: robots }) => {
  const store = useStore();
  const [isProcessing, setProcessing] = useState(false);
  const [targetLocation, setTargetLocation] = useState(null);
  const inputRefs = useRef({
    height: useRef(),
  }).current;
  const flightControlCommand = useFlightControlCommand();
  const map = OlMap.getMap();

  useEffect(() => {
    map.getTargetElement().style.cursor = 'crosshair';
    map.on('click', clickOnMap);

    return () => {
      map.getTargetElement().style.cursor = 'default';
      map.un('click', clickOnMap);
    };
  }, [robots]);

  //#region Process Functions
  const process = async (robot) => {
    const { lat, lng } = targetLocation;

    const height = Number(inputRefs.height.current.value);
    const dockHeight = store.getState().telemetry[robot.id].dockOsdStatus.height;
    const targetHeight = height + dockHeight;

    const isLiveControlMode =
      store.getState().telemetry[robot.id].droneOsd.mode_code === MODE.DJI.LIVE_FLIGHT_CONTROLS.id;

    try {
      // Live Control 모드인 경우 (Goto 명령 진행 중)
      if (isLiveControlMode) {
        await executeDJICommand(robot, flightControlCommand.gotoUpdate(targetHeight, lat, lng));
      } else {
        await executeDJICommand(robot, flightControlCommand.goto(targetHeight, lat, lng));
      }

      onSuccess(robot);
    } catch (error) {
      if (typeof error === 'object' && error?.data?.message) {
        console.debug(error.data.message);
      }
      onFailure(robot);
    }
  };
  //#endregion

  //#region Result Handlers
  // 성공 시 처리
  const onSuccess = (robot) => {
    OlMap.addGotoMarker(robot.id, targetLocation, robot.color);

    notifier.success(`${robot.name} is moving to the target location.`);
    setProcessing(false);
  };

  // 실패 시 처리
  const onFailure = (robot) => {
    notifier.error(`Failed to move ${robot.name} to the target location.`);
    setProcessing(false);
  };
  //#endregion

  useUpdateEffect(() => {
    if (!isProcessing) return;

    process(robots[0]);
  }, [isProcessing]);

  const clickOnMap = (e) => {
    const [lng, lat] = toLonLat(e.coordinate);

    setTargetLocation({ lat, lng });
    setProcessing(true);
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;

    inputRefs[name].current.value = Number(value).toFixed(1);
  };

  return (
    <div className={cx('container')}>
      <div className={cx('form')}>
        <LabelGroup htmlFor="height" label="Height">
          <div className={cx('fieldWrapper')}>
            <FieldNumber id="height" ref={inputRefs.height} name="height" defaultValue={50} onBlur={handleBlur} />
            <div className={cx('unit')}>m</div>
          </div>
        </LabelGroup>
      </div>
      <div className={cx('desc')}>Click on a location on the map where you want to move</div>
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Goto';
Command.displayMode = 'sidebar';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiArrowRightUpLine;
Command.Panel = Panel;

export default Command;
